// MemoriaBoard — el tablero de memoria (cabecera Tiempo/Premio + grilla 3x4)
// sobre el lienzo de referencia 1080x1920. Markup COMPARTIDO entre la pantalla
// EN VIVO (routes/Memoria.tsx) y el PREVIEW del admin (JuegoPreview) -> mismas
// medidas, el preview coincide con la pantalla real. Solo presentacional: el
// estado del juego (fase, grid, timers, FSM) vive en routes/Memoria.tsx.
//
// Tamaño de carta sobre el lienzo (no sobre window) para que la proporción sea
// idéntica en vivo y en preview. 200px -> 3 cols + gaps ~624px de 1080.
// Migration 039: ahora es CONFIGURABLE (deslizador en Ajustes › Juegos ›
// Memoria). Esta constante quedó como DEFAULT/fallback (= el default del SQL);
// el valor real llega por prop `cardSize` desde state.global.memoria_carta_px
// (vivo) o el form del admin (preview).
export const MEMORIA_CARD_SIZE = 200;

export type MemoriaCasilla = {
  id: number;
  imagen_idx: number;
  estado: "oculta" | "revelada" | "matcheada";
};

export function MemoriaBoard({
  tiempoDisplay,
  premioNombre,
  mostrarMemorizar,
  grid,
  imagenesUrl,
  winFlashOn = false,
  clickeable = false,
  onCardPointerDown,
  cardSize = MEMORIA_CARD_SIZE,
}: {
  /** Número grande de "Tiempo: X s". */
  tiempoDisplay: number;
  /** Nombre del premio elegido (cabecera). Opcional (preview puede omitirlo). */
  premioNombre?: string | null;
  /** Muestra "Memorizá las posiciones...". */
  mostrarMemorizar: boolean;
  grid: MemoriaCasilla[];
  imagenesUrl: Array<string | null>;
  /** Parpadeo dorado de las casillas matcheadas al ganar. */
  winFlashOn?: boolean;
  /** true solo en fase 'jugando' (en vivo). En preview / otras fases, false. */
  clickeable?: boolean;
  /** Handler de toque de carta (solo en vivo). */
  onCardPointerDown?: (idx: number) => void;
  /** Migration 039: lado de cada carta en px (configurable). Default = la
   *  constante (= default del SQL). */
  cardSize?: number;
}) {
  return (
    <div className="flex flex-col items-center gap-4">
      {/* Cabecera con tiempo + premio. */}
      <div className="flex items-center gap-4 font-display">
        <div className="rounded-lg bg-white/10 px-4 py-2">
          Tiempo: <strong className="text-2xl">{tiempoDisplay}</strong>s
        </div>
        {premioNombre && (
          <div className="rounded-lg bg-[#a78bfa]/15 px-4 py-2">
            Premio: <strong>{premioNombre}</strong>
          </div>
        )}
      </div>

      {mostrarMemorizar && (
        <div className="font-body text-sm opacity-70">
          Memorizá las posiciones...
        </div>
      )}

      {/* Grid 3 cols × 4 filas. Mismo touch-handling que el original
          (onPointerDown + preventDefault + touch-action:manipulation). */}
      <div className="grid grid-cols-3 gap-3">
        {grid.map((c, idx) => {
          const disabled = !clickeable || c.estado !== "oculta";
          return (
            <button
              key={c.id}
              type="button"
              onPointerDown={
                clickeable && onCardPointerDown
                  ? (e) => {
                      e.preventDefault();
                      onCardPointerDown(idx);
                    }
                  : undefined
              }
              disabled={disabled}
              style={{
                touchAction: "manipulation",
                width: cardSize,
                height: cardSize,
              }}
              className={`overflow-hidden rounded-xl border-2 transition ${
                c.estado === "oculta"
                  ? "border-white/20 bg-[#1a1540] hover:bg-[#2a2560]"
                  : c.estado === "matcheada"
                  ? winFlashOn
                    ? "border-yellow-300 bg-yellow-300/30 shadow-lg shadow-yellow-300/40"
                    : "border-emerald-500/50 bg-emerald-500/10"
                  : "border-[#a78bfa] bg-white/5"
              }`}
            >
              {c.estado !== "oculta" && imagenesUrl[c.imagen_idx] && (
                <img
                  src={imagenesUrl[c.imagen_idx] as string}
                  alt=""
                  className="h-full w-full object-contain p-2"
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
